import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizardContext } from '../../contexts/useWizardContext';
import { ProductionScheduleControllerService } from '../../api/services/ProductionScheduleControllerService';
import { OpenAPI } from '../../api/core/OpenAPI';
import { accessTokenStore } from '../../auth/accessTokenStore';
import type { ProductionScheduleDto } from '../../api';

export type PlanningModeType = 'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING';

export type Step0PlanningModeProps = {
  /** When set, these values override the context (ensures a reliable display inside the dialog). */
  planningMode?: PlanningModeType;
  /** Display mode (which card is selected / which extra fields). Comes from the dialog state. */
  selectedMode?: PlanningModeType;
  /** Passed in by the dialog; updates the dialog state + the context when a card is clicked. */
  onPlanningModeSelect?: (mode: PlanningModeType) => void;
  sourceScheduleUuid?: string | null;
  frozenZoneEnd?: string | null;
};

const CARD_MIN_HEIGHT_PX = 420;

/** Mini Gantt inside the wizard cards (CD: Process Teal) - not the production Gantt/schedule view. */
const WIZARD_PREVIEW_BAR = '#18A0A5';

/** TODO (open item): center the SVG diagrams in the three cards horizontally/vertically inside the image boxes (e-card-image). Flex + width:auto/inline styles did not take effect in the current setup; consider a global CSS rule for .e-card-image or a Syncfusion override. */

const Step0PlanningMode: React.FC<Step0PlanningModeProps> = (props) => {
  const { t, i18n } = useTranslation(['common']);
  const ctx = useWizardContext();
  const planningModeFromContext = (props.planningMode ?? ctx.planningMode ?? 'NEW_PLANNING') as PlanningModeType;
  const setPlanningMode = ctx.setPlanningMode;
  const setSourceScheduleUuid = ctx.setSourceScheduleUuid;
  const setSourceSchedulePlannedStart = ctx.setSourceSchedulePlannedStart;
  const updateFormData = ctx.updateFormData;
  const setStep0ReleasedPlansState = ctx.setStep0ReleasedPlansState;

  const [releasedSchedules, setReleasedSchedules] = useState<ProductionScheduleDto[]>([]);
  const [activeSchedule, setActiveSchedule] = useState<ProductionScheduleDto | null>(null);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const umplanungCardRef = useRef<HTMLDivElement>(null);
  const onSelectReplanningRef = useRef<() => void>(() => {});
  onSelectReplanningRef.current = () => {
    if (!activeSchedule?.uuid) return;
    setSourceScheduleUuid(activeSchedule.uuid);
    setSourceSchedulePlannedStart(activeSchedule.plannedStart ?? null);
    // Replan inherits BOTH plannedStart and plannedEnd from the source plan, so the demand
    // filter [plannedStart, plannedEnd] covers exactly the source plan's day(s). frozenZoneEnd
    // is a separate timestamp that only marks where running operations are fixed - it must NOT
    // override plannedStart, otherwise demands from days before the source plan get pulled in.
    if (activeSchedule.plannedStart) updateFormData('plannedStart', activeSchedule.plannedStart);
    if (activeSchedule.plannedEnd) updateFormData('plannedEnd', activeSchedule.plannedEnd);
    (props.onPlanningModeSelect ?? setPlanningMode)('REPLANNING');
  };

  const planningMode = (props.selectedMode ?? planningModeFromContext);
  const handleSelectMode = props.onPlanningModeSelect ?? ((mode: PlanningModeType) => setPlanningMode(mode));

  const handleCardSelect = (mode: PlanningModeType) => {
    if (mode === 'REPLANNING' && !activeSchedule) return;
    if (mode === 'CONTINUATION' && releasedSchedules.length === 0) return;
    if (mode === 'REPLANNING' && activeSchedule?.uuid) {
      setSourceScheduleUuid(activeSchedule.uuid);
      setSourceSchedulePlannedStart(activeSchedule.plannedStart ?? null);
      if (activeSchedule.plannedStart) updateFormData('plannedStart', activeSchedule.plannedStart);
      if (activeSchedule.plannedEnd) updateFormData('plannedEnd', activeSchedule.plannedEnd);
    }
    handleSelectMode(mode);
  };

  // Once on mount: load the released schedules (for follow-up planning) + the active schedule (for replanning).
  // Active schedule: first GET /api/production-schedules/active (200 with a null body when none
  // exists); on a network/auth error fall back to GET /api/scheduling/released-schedule.
  useEffect(() => {
    let cancelled = false;
    setLoadingSchedules(true);
    setStep0ReleasedPlansState(true, 0);

    const loadActiveSchedule = async (): Promise<ProductionScheduleDto | null> => {
      try {
        return await ProductionScheduleControllerService.getActiveSchedule();
      } catch {
        try {
          const base = OpenAPI.BASE || '';
          const headers: Record<string, string> = { 'Content-Type': 'application/json' };
          const storedTenant = localStorage.getItem('selectedTenant');
          if (storedTenant) {
            const tenant = JSON.parse(storedTenant);
            headers['X-Tenant-ID'] = tenant.uuid;
          }
          const token = accessTokenStore.get();
          if (token) headers['Authorization'] = `Bearer ${token}`;
          const res = await fetch(`${base}/api/scheduling/released-schedule`, { headers, credentials: 'include' });
          if (!res.ok) return null;
          const text = await res.text();
          const data = text ? JSON.parse(text) : null;
          if (!data) return null;
          const uuid = data.scheduleUuid ?? data.uuid;
          if (!uuid) return null;
          return { uuid, scheduleName: data.scheduleName ?? '' } as ProductionScheduleDto;
        } catch {
          return null;
        }
      }
    };

    Promise.all([
      ProductionScheduleControllerService.findAll().then((list) =>
        (list || []).filter((s) => s.status === 'released' || s.status === 'active')
      ),
      loadActiveSchedule()
    ]).then(([released, active]) => {
      if (cancelled) return;
      setReleasedSchedules(released);
      setActiveSchedule(active ?? null);
      setStep0ReleasedPlansState(false, released.length);
    }).catch(() => {
      if (cancelled) return;
      setReleasedSchedules([]);
      setActiveSchedule(null);
      setStep0ReleasedPlansState(false, 0);
    }).finally(() => {
      if (!cancelled) setLoadingSchedules(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only once on mount
  }, []);

  // Native click listener on the replanning card to detect whether the click reaches the DOM element (the Syncfusion dialog may block React events)
  useEffect(() => {
    const el = umplanungCardRef.current;
    if (!el) return;
    const handler = (e: Event) => {
      if (e.type === 'click') {
        const disabled = el.classList.contains('opacity-60') || el.getAttribute('aria-disabled') === 'true';
        if (!disabled) onSelectReplanningRef.current();
      }
    };
    el.addEventListener('click', handler, true);
    el.addEventListener('mousedown', handler, true);
    return () => {
      el.removeEventListener('click', handler, true);
      el.removeEventListener('mousedown', handler, true);
    };
  }, []);

  /** Last released/active schedule (sorted by end) - the base for follow-up planning */
  const lastReleasedForContinuation = useMemo(() => {
    if (releasedSchedules.length === 0) return null;
    const sorted = [...releasedSchedules].sort((a, b) => {
      const endA = a.plannedEnd ? new Date(a.plannedEnd).getTime() : 0;
      const endB = b.plannedEnd ? new Date(b.plannedEnd).getTime() : 0;
      return endB - endA;
    });
    return sorted[0];
  }, [releasedSchedules]);

  // Bar lengths per lane [first, second]: top row first long/second short, middle row second longer, bottom row both about equal, the bottom row is the longest. (half length for a compact display)
  const WIDTHS_FIRST_LONGER: number[] = [13, 7, 7, 11, 12, 12];
  // Different lengths for the new (blue) part in follow-up/replanning, directly adjoining the grey part
  const WIDTHS_BLUE: number[] = [8, 6, 14, 5, 10, 10];

  // Gantt frame. viewBox 0 0 120 25 for better centering without white margins; w = width of the frame (54 or 68).
  const ganttFrame = (x: number, w: number = 54) => (
    <>
      <rect x={x} y="0" width={w} height="28" fill="#fafafa" stroke="#e2e8f0" strokeWidth="1" rx="2" />
      <line x1={x + 2} y1="26" x2={x + w - 2} y2="26" stroke="#94a3b8" strokeWidth="0.8" />
      {[0, 1, 2, 3].map((i) => (<line key={i} x1={x + 2 + (i * (w - 4)) / 3} y1="26" x2={x + 2 + (i * (w - 4)) / 3} y2="28" stroke="#64748b" strokeWidth="0.5" />))}
      {[4, 8, 12, 16, 20, 24].map((rowY) => (
        <line key={rowY} x1={x + 2} y1={rowY + 2} x2={x + w - 2} y2={rowY + 2} stroke="#e2e8f0" strokeWidth="0.5" />
      ))}
    </>
  );

  // 6 bars exactly in a row (no gap): 3 lanes, 2 operations each on one line. widths = [w0,w1, w2,w3, w4,w5]
  const barsSequentialPerLane = (x0: number, color: string, widths: number[]) => {
    const laneY = [5, 13, 21]; // one row per lane, bars exactly in a row
    return laneY.flatMap((y, lane) => {
      const w1 = widths[lane * 2];
      const w2 = widths[lane * 2 + 1];
      return [
        <rect key={`${lane}-0`} x={x0} y={y} width={w1} height="3" rx="1" fill={color} />,
        <rect key={`${lane}-1`} x={x0 + w1} y={y} width={w2} height="3" rx="1" fill={color} />,
      ];
    });
  };

  // Grey (old plan) + blue (new plan) per lane, blue adjoins the grey directly; different lengths for blue.
  const barsGrayThenBlue = (x0: number, grayWidths: number[], blueWidths: number[]) => {
    const laneY = [5, 13, 21];
    return laneY.flatMap((y, lane) => {
      const g1 = grayWidths[lane * 2];
      const g2 = grayWidths[lane * 2 + 1];
      const b1 = blueWidths[lane * 2];
      const b2 = blueWidths[lane * 2 + 1];
      return [
        <rect key={`${lane}-g1`} x={x0} y={y} width={g1} height={3} rx={1} fill="#64748b" />,
        <rect key={`${lane}-g2`} x={x0 + g1} y={y} width={g2} height={3} rx={1} fill="#64748b" />,
        <rect key={`${lane}-b1`} x={x0 + g1 + g2} y={y} width={b1} height={3} rx={1} fill={WIZARD_PREVIEW_BAR} />,
        <rect key={`${lane}-b2`} x={x0 + g1 + g2 + b1} y={y} width={b2} height={3} rx={1} fill={WIZARD_PREVIEW_BAR} />,
      ];
    });
  };


  // Like barsFrozenGrayThenBlue, but several grey segments per lane (for example resource 2: two operations [7, 11]).
  const barsFrozenGrayMultiThenBlue = (x0: number, graySegmentsPerLane: number[][], blueWidths: number[]) => {
    const laneY = [5, 13, 21];
    return laneY.flatMap((y, lane) => {
      const segments = graySegmentsPerLane[lane];
      const grayTotal = segments.reduce((a, b) => a + b, 0);
      const b1 = blueWidths[lane * 2];
      const b2 = blueWidths[lane * 2 + 1];
      const grayRects = segments.map((w, i) => {
        const gx = x0 + segments.slice(0, i).reduce((a, b) => a + b, 0);
        return <rect key={`${lane}-g${i}`} x={gx} y={y} width={w} height={3} rx={1} fill="#64748b" />;
      });
      return [
        ...grayRects,
        <rect key={`${lane}-b1`} x={x0 + grayTotal} y={y} width={b1} height={3} rx={1} fill={WIZARD_PREVIEW_BAR} />,
        <rect key={`${lane}-b2`} x={x0 + grayTotal + b1} y={y} width={b2} height={3} rx={1} fill={WIZARD_PREVIEW_BAR} />,
      ];
    });
  };

  return (
    <div className="flex flex-col gap-6 p-4 w-full" style={{ minHeight: CARD_MIN_HEIGHT_PX + 48 }}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch" style={{ gridAutoRows: `minmax(${CARD_MIN_HEIGHT_PX}px, auto)` }} role="group" aria-label={t('productionSchedule.wizard.step0.title', 'Planning Type')}>
        {/* Fresh planning: empty Gantt -> filled Gantt */}
        <div
          style={{ minHeight: CARD_MIN_HEIGHT_PX, position: 'relative', zIndex: 10 }}
          className={`e-card flex flex-col gap-3 p-4 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-colors overflow-visible items-start justify-start ${planningMode === 'NEW_PLANNING' ? 'pb-e-card--selected' : 'border-gray-200'}`}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleCardSelect('NEW_PLANNING'); }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCardSelect('NEW_PLANNING'); }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardSelect('NEW_PLANNING'); } }}
          role="button"
          tabIndex={0}
        >
          <div className="e-card-image w-full h-20 rounded overflow-hidden bg-white border border-gray-200 flex-shrink-0" aria-hidden style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 120 25" preserveAspectRatio="xMidYMid meet" style={{ height: '100%', width: '90%' }}>
              <g>{ganttFrame(0)}</g>
              <path d="M 58 14 L 62 14" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
              <g>{ganttFrame(66)}{barsSequentialPerLane(70, WIZARD_PREVIEW_BAR, WIDTHS_FIRST_LONGER)}</g>
            </svg>
          </div>
          <div className="e-card-header min-w-0 flex flex-col flex-shrink-0 justify-start self-stretch">
            <div className="e-card-header-caption text-left">
              <div className="text-lg font-bold tracking-tight text-gray-900">
                {t('productionSchedule.wizard.step0.optionNew', 'New Planning')}
              </div>
              <p className="e-card-content text-sm text-gray-500 mt-1.5">
                {t('productionSchedule.wizard.step0.optionNewDesc', 'Create a new production schedule for a period (first schedule or independent of existing schedules).')}
              </p>
            </div>
          </div>
          <div className="flex-1 min-h-2 w-full" aria-hidden />
        </div>

        {/* Follow-up planning: selectable only once the schedules are loaded and at least one released schedule exists */}
        {(() => {
          const continuationDisabled = loadingSchedules || releasedSchedules.length === 0;
          return (
        <div
          style={{ minHeight: CARD_MIN_HEIGHT_PX, position: 'relative', zIndex: 10 }}
          className={`e-card flex flex-col gap-3 p-4 rounded-lg border-2 transition-colors overflow-visible items-start justify-start ${planningMode === 'CONTINUATION' ? 'pb-e-card--selected' : 'border-gray-200'} ${continuationDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
          onMouseDown={(e) => { if (continuationDisabled) return; e.preventDefault(); e.stopPropagation(); handleCardSelect('CONTINUATION'); }}
          onClick={(e) => { if (continuationDisabled) return; e.preventDefault(); e.stopPropagation(); handleCardSelect('CONTINUATION'); }}
          onKeyDown={(e) => { if (continuationDisabled) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardSelect('CONTINUATION'); } }}
          role="button"
          tabIndex={continuationDisabled ? -1 : 0}
          aria-disabled={continuationDisabled}
        >
          <div className="e-card-image w-full h-20 rounded overflow-hidden bg-white border border-gray-200 flex-shrink-0" aria-hidden style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 130 25" preserveAspectRatio="xMidYMid meet" style={{ height: '100%', width: '90%' }}>
              <g>{ganttFrame(0)}{barsSequentialPerLane(4, '#64748b', WIDTHS_FIRST_LONGER)}</g>
              <path d="M 58 14 L 62 14" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
              <g>{ganttFrame(66, 54)}{barsGrayThenBlue(70, WIDTHS_FIRST_LONGER, WIDTHS_BLUE)}</g>
            </svg>
          </div>
          <div className="e-card-header min-w-0 flex flex-col flex-shrink-0 justify-start self-stretch">
            <div className="e-card-header-caption text-left">
              <div className="text-lg font-bold tracking-tight text-gray-900">
                {t('productionSchedule.wizard.step0.optionContinuation', 'Continuation')}
              </div>
              <p className="e-card-content text-sm text-gray-500 mt-1.5">
                {t('productionSchedule.wizard.step0.optionContinuationDesc', 'Create a schedule for the period after the last released/active schedule. The new schedule connects seamlessly.')}
              </p>
            </div>
          </div>
          {/* Extra field for follow-up planning: base schedule + a short hint with a tooltip */}
          <div className="flex-shrink-0 mt-2 pt-2 border-t border-gray-200 w-full space-y-1">
            {lastReleasedForContinuation ? (
              <p className="text-sm text-gray-700">
                {t('productionSchedule.wizard.step0.continuationBasis', 'Connect to')}: <strong>{lastReleasedForContinuation.scheduleName ?? lastReleasedForContinuation.uuid}</strong>
                {lastReleasedForContinuation.status && (
                  <span className="text-gray-500"> ({lastReleasedForContinuation.status === 'active' ? t('productionSchedule.wizard.step0.statusActive', 'active') : t('productionSchedule.wizard.step0.statusReleased', 'released')})</span>
                )}
                {lastReleasedForContinuation.plannedEnd && (
                  <span className="text-gray-500"> ({t('productionSchedule.wizard.step0.planEnd', 'Plan end')}: {new Date(lastReleasedForContinuation.plannedEnd).toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })})</span>
                )}
              </p>
            ) : loadingSchedules ? (
              <p className="text-sm text-gray-500">{t('productionSchedule.wizard.step0.loadingPlans', 'Loading plans…')}</p>
            ) : (
              <p className="text-sm text-amber-600">{t('productionSchedule.wizard.step0.noPlanForContinuation', 'No released or active schedule available.')}</p>
            )}
            <p className="text-sm text-gray-700">
              {t('productionSchedule.wizard.step0.continuationHintShort', 'Connection per resource after the last operation.')}
              <span
                className="inline-block ml-1 pb-syncfusion-accent-text cursor-help align-middle"
                title={t('productionSchedule.wizard.step0.continuationHintTooltip', 'The new schedule is planned on each resource after the last operation of the released schedule (not at the end of the entire schedule).')}
                aria-label={t('productionSchedule.wizard.step0.continuationHintTooltip', 'The new schedule is planned on each resource after the last operation of the released schedule (not at the end of the entire schedule).')}
              >
                ⓘ
              </span>
            </p>
          </div>
          <div className="flex-1 min-h-2 w-full" aria-hidden />
        </div>
          );
        })()}

        {/* Replanning: selectable only when an active schedule exists (only one per tenant - nothing to choose) */}
        {(() => {
          const umplanungDisabled = loadingSchedules || !activeSchedule;
          return (
        <div
          ref={umplanungCardRef}
          data-wizard-card="REPLANNING"
          style={{ minHeight: CARD_MIN_HEIGHT_PX, position: 'relative', zIndex: 10 }}
          className={`e-card flex flex-col gap-3 p-4 rounded-lg border-2 transition-colors overflow-visible items-start justify-start ${planningMode === 'REPLANNING' ? 'pb-e-card--selected' : 'border-gray-200'} ${umplanungDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}
          onMouseDown={(e) => {
            if (umplanungDisabled) return;
            e.preventDefault();
            e.stopPropagation();
            handleCardSelect('REPLANNING');
          }}
          onClick={(e) => {
            if (umplanungDisabled) return;
            e.preventDefault();
            e.stopPropagation();
            handleCardSelect('REPLANNING');
          }}
          onKeyDown={(e) => { if (umplanungDisabled) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardSelect('REPLANNING'); } }}
          role="button"
          tabIndex={umplanungDisabled ? -1 : 0}
          aria-disabled={umplanungDisabled}
        >
          <div className="e-card-image w-full h-20 rounded overflow-hidden bg-white border border-gray-200 flex-shrink-0" aria-hidden style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <svg viewBox="0 0 120 25" preserveAspectRatio="xMidYMid meet" style={{ height: '100%', width: '90%' }}>
              <g>{ganttFrame(0)}<line x1="13" y1="0" x2="13" y2="28" stroke="#64748b" strokeWidth="0.6" strokeDasharray="2 1.5" />{barsSequentialPerLane(4, '#64748b', WIDTHS_FIRST_LONGER)}</g>
              <path d="M 58 14 L 62 14" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
              <g>{ganttFrame(66)}<line x1="79" y1="0" x2="79" y2="28" stroke="#64748b" strokeWidth="0.6" strokeDasharray="2 1.5" />{barsFrozenGrayMultiThenBlue(70, [[13], [7, 11], [12]], WIDTHS_BLUE)}</g>
            </svg>
          </div>
          <div className="e-card-header min-w-0 flex flex-col flex-shrink-0 justify-start self-stretch">
            <div className="e-card-header-caption text-left">
              <div className="text-lg font-bold tracking-tight text-gray-900">
                {t('productionSchedule.wizard.step0.optionReplan', 'Replanning')}
              </div>
              <p className="e-card-content text-sm text-gray-500 mt-1.5">
                {t('productionSchedule.wizard.step0.optionReplanDesc', 'Partially replace an existing released/active schedule (Frozen Zone: running operations stay, the rest is replanned).')}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 flex-shrink-0 w-full space-y-1">
            {activeSchedule ? (
              <p className="text-sm text-gray-700">
                {t('productionSchedule.wizard.step0.replanActivePlan', 'Replanning the active schedule')}: <strong>{activeSchedule.scheduleName ?? activeSchedule.uuid}</strong>
              </p>
            ) : loadingSchedules ? (
              <p className="text-sm text-gray-500">{t('productionSchedule.wizard.step0.loadingPlans', 'Loading plans…')}</p>
            ) : (
              <p className="text-sm text-amber-600">
                {t('productionSchedule.wizard.step0.noActivePlanForReplan', 'No active schedule. Only the currently running (active) schedule can be replanned.')}
                {releasedSchedules.length > 0 && (
                  <span className="block mt-1 text-amber-700">
                    {t('productionSchedule.wizard.step0.releasedButNotActiveHint', 'There are released schedules; for replanning a schedule must be activated first.')}
                  </span>
                )}
              </p>
            )}
            <p className="text-sm text-gray-700 mt-1">
              {t('productionSchedule.wizard.step0.frozenZoneStep1Short', 'Frozen zone end selectable in the next step (Production quantities tab).')}
              <span
                className="inline-block ml-1 pb-syncfusion-accent-text cursor-help align-middle"
                title={t('productionSchedule.wizard.step0.frozenZoneInStep1Hint', 'Select the frozen zone end in the next step (Production quantities tab) to see actual, old target, new target and remaining interactively.')}
                aria-label={t('productionSchedule.wizard.step0.frozenZoneInStep1Hint', 'Select the frozen zone end in the next step (Production quantities tab) to see actual, old target, new target and remaining interactively.')}
              >
                ⓘ
              </span>
            </p>
          </div>
          <div className="flex-1 min-h-2 w-full" aria-hidden />
        </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Step0PlanningMode;
